import React, { useState } from 'react';
import './App.css';

// Header Component
function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="header">
      <div className="container">
        <div className="nav-wrapper">
          <div className="logo">
            <img src="/logo.png" alt="GigSense" className="logo-img" />
          </div>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            ☰
          </button>

          <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#enterprise" onClick={() => setMobileMenuOpen(false)}>Enterprise</a>
            <a href="#waitlist" onClick={() => setMobileMenuOpen(false)}>Join Waitlist</a>
            <a href="#waitlist" className="nav-cta" onClick={() => setMobileMenuOpen(false)}>Get Started</a>
          </nav>
        </div>
      </div>
    </header>
  );
}

// Hero Section
function Hero() {
  return (
    <section className="hero">
      <div className="container">
        <div className="hero-content">
          <h1 className="hero-headline">Your Gigs. Your Earnings. Your Rewards.</h1>
          <p className="hero-social-proof">Built for platforms managing 100K+ drivers</p>

          {/* Hero Image - New GigSense Graphic */}
          <div className="hero-image-container">
            <img
              src={`${process.env.PUBLIC_URL}/gigsense_graphic.png`}
              alt="GigSense - Rewards your way. Earnings Aggregator, Income Verification, Portable Reward Hours."
              className="hero-image"
            />
          </div>

          {/* CTA buttons */}
          <div className="hero-cta">
            <a href="#waitlist" className="btn btn-primary btn-large">Start Earning Today</a>
            <a href="#enterprise" className="btn btn-secondary btn-large">For Enterprise</a>
          </div>

          {/* Stats */}
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-number">$42B</div>
              <div className="stat-label">Market Opportunity</div>
            </div>
            <div className="stat">
              <div className="stat-number">59M+</div>
              <div className="stat-label">Gig Workers in US</div>
            </div>
            <div className="stat">
              <div className="stat-number">3</div>
              <div className="stat-label">Core Features</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Solution - 3 Core Pillars
function Solution({ onImageClick }) {
  const pillars = [
    { src: `${process.env.PUBLIC_URL}/pillar_earnings.png`, alt: "Tracking All Your Earnings? GigSense Earnings Aggregation" },
    { src: `${process.env.PUBLIC_URL}/pillar_verification.png`, alt: "Proving Your Earnings? GigSense Detailed Income Verification" },
    { src: `${process.env.PUBLIC_URL}/pillar_rewards.png`, alt: "Missing Out on Rewards You've Earned? GigSense Portability of Rewards" },
  ];

  return (
    <section id="features" className="solution">
      <div className="container">
        <div className="pillar-section-header">
          <h2>Earnings. Verification. Reward Hours.</h2>
          <p>Everything gig workers need to get the rewards they deserve</p>
        </div>

        <div className="pillar-grid">
          {pillars.map((pillar, i) => (
            <div className="pillar-card" key={i} onClick={() => onImageClick(pillar)}>
              <img src={pillar.src} alt={pillar.alt} className="pillar-image" />
              <div className="pillar-tap-hint">Tap to enlarge</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Enterprise Section
function Enterprise({ onImageClick }) {
  const imgData = {
    src: `${process.env.PUBLIC_URL}/enterprise_graphic.png`,
    alt: "GigSense Enterprise - AI Business Intelligence for driver retention, throughput, fleet performance, and intelligent fleet reward campaigns"
  };

  return (
    <section id="enterprise" className="enterprise">
      <div className="container">
        <div className="pillar-section-header">
          <h2>Enterprise Intelligence</h2>
          <p>AI-powered fleet analytics that drive improved long term fleet health and performance</p>
        </div>
        <div className="enterprise-content">
          <div className="enterprise-image-container" onClick={() => onImageClick(imgData)}>
            <img src={imgData.src} alt={imgData.alt} className="enterprise-image" />
            <div className="pillar-tap-hint">Tap to enlarge</div>
          </div>
          <a href="#waitlist" className="btn btn-primary btn-large">Request Enterprise Demo</a>
        </div>
      </div>
    </section>
  );
}

// Waitlist Section
function Waitlist() {
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('worker');
  const [companyName, setCompanyName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Google Apps Script web app URL
      const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxd3nf3A_NvzptxRFPjTxlIre76Snbmo0BoJ_WnICPKvPWQheuh4okumjjmX6jFBawuyg/exec';

      const payload = {
        email: email,
        userType: userType
      };
      if (userType === 'enterprise') {
        payload.companyName = companyName;
      }

      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setEmail('');
        setCompanyName('');
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setEmail('');
        setCompanyName('');
      }, 3000);
    }
  };

  return (
    <section id="waitlist" className="waitlist">
      <div className="container">
        <div className="waitlist-content">
          <h2>Start Earning Today</h2>
          <p>Be among the first to get the rewards you deserve from all your gigs.</p>
          
          {submitted ? (
            <div className="waitlist-success">
              <div className="success-icon">✓</div>
              <h3>You're on the list!</h3>
              <p>We'll notify you when GigSense launches.</p>
            </div>
          ) : (
            <form className="waitlist-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="userType">I am a...</label>
                <select 
                  id="userType"
                  value={userType} 
                  onChange={(e) => setUserType(e.target.value)}
                  className="form-select"
                >
                  <option value="worker">Gig Worker</option>
                  <option value="enterprise">Enterprise Partner</option>
                  <option value="investor">Potential Investor</option>
                  <option value="other">Just Interested</option>
                </select>
              </div>
              {userType === 'enterprise' && (
                <div className="form-group">
                  <label htmlFor="companyName">Company Name</label>
                  <input
                    id="companyName"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your company name"
                    required
                    className="form-input"
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  className="form-input"
                />
              </div>
              <button type="submit" className="btn btn-primary btn-large btn-full">
                Join Waitlist
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content-centered">
          <div className="footer-brand-centered">
            <img src="/logo.png" alt="GigSense" className="footer-logo" />
            <p className="footer-tagline">Rewards your way</p>
          </div>
          <div className="footer-links-centered">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#waitlist">Join Waitlist</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="#about">About Us</a>
              <a href="#press">Press</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 GigSense Inc. | Contact: hello@gigsense.net</p>
        </div>
      </div>
    </footer>
  );
}

// Lightbox Component
function Lightbox({ src, alt, onClose }) {
  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>&times;</button>
      <img src={src} alt={alt} className="lightbox-image" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

// Main App Component
function App() {
  const [lightboxImage, setLightboxImage] = useState(null);

  return (
    <div className="App">
      <Header />
      <Hero />
      <Solution onImageClick={setLightboxImage} />
      <Enterprise onImageClick={setLightboxImage} />
      <Waitlist />
      <Footer />
      {lightboxImage && (
        <Lightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}

export default App;
